import {
  DEFERRED_TASK_MODIFIER_KEY,
  type TaskModifierType,
  TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY,
} from '@kaiizen/kai-pro-api';

/**
 * The copy for the three Kai types, transcribed from the design mockup's `TYPES` object.
 *
 * Type names follow the acceptance criteria (Tracked Cost, Procure on Change Order) and are used
 * on this page only. They are deliberately *not* the modifiers' default display names: those stay
 * `Resident Responsible` and `Deferred` for current customers, per the 2026-10-01 standup, and
 * renaming them is a follow-up after deployment. See the spec.
 */
export type ModifierTypeCopy = {
  name: string;
  /** Parenthetical beside the name — what the type does to money. */
  kind: string;
  /** One-line summary under the title; Display has none, having no financial rules. */
  meta?: string;
  /** Block header under the lock icon. */
  lock: string;
  /** Display's second line, in place of the rule rows. */
  lockSub?: string;
  rules?: readonly { title: string; description: string; on: boolean }[];
  /** How many of this type an org may have. */
  max: number;
  /** Whether a newly created modifier of this type lets field users apply it. */
  fieldUsersDefault: boolean;
};

export const MODIFIER_TOTAL_MAX = 10;

/**
 * The key and line-item tag a financial modifier must use.
 *
 * Financial behaviour is resolved by fixed tag name, not by type: `SCOPING_TAGS` in
 * `changeOrderLib` looks for `deferred`, and the reports and filters look for these two literals.
 * A financial modifier minted with a fresh tag would therefore behave like a display tag while
 * its card showed locked rules claiming otherwise — so each financial type uses its reserved key,
 * and the criteria's cap of 2 is held at 1 until the runtime resolves behaviour by type
 * (KAIDEV-3182 review). Display modifiers mint their own, since nothing downstream reads them.
 */
export const RESERVED_KEY_BY_MODIFIER_TYPE: Partial<Record<TaskModifierType, string>> = {
  deferred: DEFERRED_TASK_MODIFIER_KEY,
  resident: TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY,
};

export const MODIFIER_TYPE_COPY: Record<TaskModifierType, ModifierTypeCopy> = {
  resident: {
    name: 'Tracked Cost',
    kind: 'Affects costs & purchase orders',
    meta: 'In project total · In purchase orders · Change Orders not required',
    lock: 'These rules come from the predefined Kai Resident Responsible logic. These rules are set by Kai and cannot be changed.',
    rules: [
      { title: 'Include in project total', description: 'Counts toward the project budget and financial summary.', on: true },
      {
        title: 'Include in purchase orders',
        description: 'Ordered automatically on the purchase order created when the scope is approved.',
        on: true,
      },
      {
        title: 'Requires a change order to activate',
        description: 'Can be ordered as soon as the scope is approved. No Change Order needed.',
        on: false,
      },
    ],
    max: 1,
    fieldUsersDefault: false,
  },
  deferred: {
    name: 'Procure on Change Order',
    kind: 'Affects costs & purchase orders',
    meta: 'In project total · Not in purchase orders · Change Orders required',
    lock: 'These rules come from the predefined Kai deferred logic. These rules are set by Kai and cannot be changed.',
    rules: [
      { title: 'Include in project total', description: 'Counts toward the project budget and financial summary.', on: true },
      {
        title: 'Include in purchase orders',
        description: 'Not ordered automatically. Handle outside Kai or through a Change Order.',
        on: false,
      },
      {
        title: 'Requires a change order to activate',
        description: 'Blocked from procurement until a Change Order is approved. Prevents accidental ordering.',
        on: true,
      },
    ],
    max: 1,
    fieldUsersDefault: false,
  },
  display: {
    name: 'Display',
    kind: 'Tag only. No effect on costs.',
    lock: 'NO Financial Rules. Display modifiers are tags only.',
    lockSub: 'Does not affect project totals, purchase orders, or Change Orders. Used to organize and filter tasks.',
    max: 6,
    fieldUsersDefault: true,
  },
};

/** Reserved keys predate `type`; a stored record that omits it is one of these two. */
export const MODIFIER_TYPE_BY_RESERVED_KEY: Record<string, TaskModifierType> = {
  [DEFERRED_TASK_MODIFIER_KEY]: 'deferred',
  [TENANT_RESPONSIBILITY_TASK_MODIFIER_KEY]: 'resident',
};

export const MODIFIER_LABEL_MAX_LENGTH = 20;

export const MODIFIER_LABEL_TIP = 'Add a custom label to change how this modifier type displays in the system.';

export const MODIFIER_FIELD_USER_COPY =
  'When on, field users can tag line items with this modifier while building a scope in Kai mobile. When off, only admin can apply in Kai Web.';

export const MODIFIER_SETTINGS_NOTE = 'We recommend leaving this off for financial modifiers, which carry procurement consequences and need admin judgement.';

export const MODIFIER_DUPLICATE_LABEL_ERROR = 'That label is already in use. Give this modifier a different name.';

/**
 * A modifier's name as the org sees it: its label, or the Kai type name when it has none.
 *
 * Uniqueness compares these rather than raw `displayName`s, because an unlabelled modifier still
 * occupies its type name on screen — two cards both reading "Display" would be the same clash.
 */
export const getEffectiveModifierLabel = (
  key: string,
  modifier: { displayName?: string; type?: TaskModifierType },
): string => {
  const type = modifier.type ?? MODIFIER_TYPE_BY_RESERVED_KEY[key];
  return modifier.displayName?.trim() || (type ? MODIFIER_TYPE_COPY[type].name : key);
};

/** Case-insensitive and whitespace-trimmed, as both the add modal and inline rename compare. */
export const normalizeModifierLabel = (label: string): string => label.trim().toLowerCase();
